import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Briefcase, Settings as SettingsIcon, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import adminApi from '../../api/adminApi';
import { Button } from '../../components/ui/button';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    projects: 0,
    loading: true
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const projects = await adminApi.getProjects();
      setStats({
        projects: projects.length,
        loading: false
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ projects: 0, loading: false });
    }
  };

  const quickActions = [
    {
      title: 'Add New Project',
      description: 'Create a new portfolio project',
      icon: Plus,
      link: '/admin/projects?action=new',
      color: 'bg-orange-500'
    },
    {
      title: 'Manage Projects',
      description: 'Edit existing portfolio items',
      icon: Briefcase,
      link: '/admin/projects',
      color: 'bg-blue-500'
    },
    {
      title: 'Site Settings',
      description: 'Update company info and content',
      icon: SettingsIcon,
      link: '/admin/settings',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-black uppercase mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Manage your site content below.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-lg border-2 border-black"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold uppercase text-gray-600">Total Projects</h3>
            <Briefcase size={20} />
          </div>
          <div className="text-4xl font-black">{stats.loading ? '...' : stats.projects}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-lg border-2 border-black"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold uppercase text-gray-600">Status</h3>
            <TrendingUp size={20} className="text-green-600" />
          </div>
          <div className="text-2xl font-black text-green-600">LIVE</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-black text-white p-6 rounded-lg"
        >
          <h3 className="text-sm font-bold uppercase mb-2">Quick Tip</h3>
          <p className="text-sm">Upload images for best quality or paste image URLs for speed</p>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-black uppercase mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={action.link}
                  className="block bg-white p-6 rounded-lg border-2 border-gray-200 hover:border-black transition-colors group"
                >
                  <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-black uppercase mb-2 group-hover:text-orange-500 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-gray-600">{action.description}</p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;